<script setup lang="ts">
import { ref } from 'vue'
import { PinInputInput, PinInputRoot } from 'radix-vue'
import { CircleHelp } from 'lucide-vue-next'
import ResendIcon from '@/assets/icons/resend.svg?component'
import { LoadingButton } from '@/components/ui/button'
import Heading from '../Heading.vue'

const props = defineProps<{
  error?: string
  countdown?: number
  onResendCode: () => void
  title?: string
  description?: string
  onComplete: (code: string[]) => void
  email?: string
  usePadding?: boolean
}>()

const verificationCode = ref<string[]>([])
const isLoading = ref(false)

const handleComplete = (code: string[]) => {
  props.onComplete(code)
}

const handleResendCode = async () => {
  isLoading.value = true
  try {
    await props.onResendCode()
  } catch (error) {
    console.error('Error resending code:', error)
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <div class="flex flex-col items-center">
    <div class="flex flex-grow flex-col justify-center">
      <Heading
        :title="title || $t('LoginPopup.label.EnterVerificationCode')"
        class="pt-16 md:pt-20"
      />

      <img
        width="312"
        height="447"
        src="/assets/login/lock_illustration.png"
        alt="Lock illustration"
        class="-mt-20 h-auto w-[312px] lg:-mt-40"
      />

      <div class="text-center text-sm font-light leading-6 text-white/70">
        Enter the 4-digit code {{ email ? 'sent to' : 'sent to your email' }}
        <br />
        <span
          class="inline-block max-w-[280px] overflow-hidden text-ellipsis whitespace-nowrap font-semibold text-white"
        >
          {{ email ? ` ${email}` : 'dipeshwagle@gmail.com' }}
        </span>
      </div>

      <div class="flex justify-center gap-2 py-5">
        <PinInputRoot
          v-model="verificationCode"
          placeholder=""
          class="flex gap-2"
          type="number"
          @complete="handleComplete"
        >
          <PinInputInput
            v-for="(_, index) in 4"
            :key="index"
            :index="index"
            class="h-12 w-12 rounded-lg border border-[#2C2D3E] bg-[#1A1F2E] text-center text-lg text-white focus:border-[#0071F2] focus:bg-[#0E1B40] focus:outline-none"
          />
        </PinInputRoot>
      </div>

      <div v-if="error" class="flex items-center justify-center gap-2 text-sm text-error">
        <CircleHelp class="h-4 w-4" />
        {{ error }}
      </div>

      <div class="mb-4 mt-8 flex w-full justify-center">
        <span v-if="countdown" class="text-sm font-normal leading-6 text-white/70">
          {{
            $t('LoginPopup.button.ResendCodeTimer', {
              time: `00:${countdown.toString().padStart(2, '0')}`
            })
          }}
        </span>
        <LoadingButton
          v-else
          class="relative h-[48px] w-full cursor-pointer rounded-[8px] border-[0.5px] border-white/10 bg-[#19233A] text-sm text-white/70"
          @click="handleResendCode"
          :loading="isLoading"
          type="button"
        >
          <ResendIcon class="absolute left-4 mr-2 size-4" />
          {{ $t('LoginPopup.button.ResendCode') }}
        </LoadingButton>
      </div>
    </div>
  </div>
</template>
